<template>
  <span class="vs-slilder">

    <VsProgressBar
      v-if="isSlidable"
      class="vs-slider__progress"
      :percentage="percentage"
    />

    <input
      class="vs-slilder__input"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :class="
        [
          (wide ? 'wide' : ''),
          (minimized ? 'minimized' : '')
        ]
      "

      :value="renderValue"
      @input="handleValue"
      @change="handleValue"
    >
  </span>
</template>

<script>
import debounce from 'lodash/debounce';
import VsProgressBar from './VsProgressBar';

export default {
  components: {
    VsProgressBar,
  },
  props: {
    value: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: [String, Number],
      default: 'any',
    },
    wide: {
      type: Boolean,
      default: false,
    },
    minimized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      renderValue: this.value,
    };
  },

  computed: {
    percentage() {
      return (this.value / this.max) * 100;
    },
    isSlidable() {
      return !(this.min === this.max);
    },
  },

  watch: {
    value(newValue, oldValue) {
      this.updateDebouncer(newValue);
    },
  },

  created() {
    this.updateDebouncer = debounce(this.updateRenderValue.bind(this), 100);
  },

  methods: {
    updateRenderValue(newValue) {
      this.renderValue = newValue;
    },

    handleValue(event) {
      this.renderValue = parseFloat(event.currentTarget.value);
      this.$emit('input', this.renderValue);
    },
  },
};
</script>

<style lang="scss">
$thumb-d: 18px;
$track-c: transparent;
$filll-c: transparent;

.vs-slilder {
  position: absolute;
  width: 100%;
  height: 100%;
}
.vs-slider__progress {
  background: #e81a65;
}

.vs-slilder__input {
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  position: absolute;
  z-index: 1;
}
.vs-slilder__input.wide {
  width: calc(100% + #{$thumb-d});
  left: calc(-#{$thumb-d} / 2);
}

// To customize input default styling
@mixin track($fill: 0) {
  box-sizing: border-box;
  border: none;
  width: 100%;
  height: 100%;
  background: $track-c;
  cursor: pointer;

  @if $fill == 1 {
    .js & {
      background: linear-gradient($filll-c, $filll-c)
        0/ var(--sx) 100% no-repeat $track-c
    }
  }
}

@mixin fill() {
  height: 100%;
  background: $filll-c
}

@mixin thumb($yOffset) {
  cursor: pointer;
  box-sizing: border-box;
  border: none;
  width: $thumb-d; height: $thumb-d;
  border-radius: 50%;
  background: white;
  transform: translate(0, #{$yOffset});
  top: 50%;
  position: relative;
  transition: transform 0.2s;
  box-shadow: 1px 1px 1px 0 rgba(black, 0.1);

  &:hover {
    transform: translate(0, #{$yOffset}) scale(1.2);
  }
  &:active {
    transform: translate(0, #{$yOffset}) scale(1.3);
  }
}

[type='range'].vs-slilder__input {
  &, &::-webkit-slider-thumb {
    -webkit-appearance: none;
  }

  --range: calc(var(--max) - var(--min));
  --ratio: calc((var(--val) - var(--min))/var(--range));
  --sx: calc(.5*#{$thumb-d} + var(--ratio)*(100% - #{$thumb-d}));
  margin: 0;
  padding: 0;
  background: transparent;
  font: 1em/1 arial, sans-serif;

  &::-webkit-slider-runnable-track { @include track(1); }
  &::-moz-range-track { @include track }
  &::-ms-track { @include track }

  &::-moz-range-progress { @include fill }
  &::-ms-fill-lower { @include fill }

  &::-webkit-slider-thumb { @include thumb(-50%); }
  &::-ms-thumb { @include thumb(0); }
  &::-moz-range-thumb { @include thumb(0); }

  &::-ms-tooltip { display: none; }
}

[type='range'].vs-slilder__input.minimized {
  &::-webkit-slider-thumb {
    opacity: 0;
  }
  &::-ms-thumb {
    opacity: 0;
  }
  &::-moz-range-thumb {
    opacity: 0;
  }
}
</style>
